import { format } from 'date-fns';
import { useAtom } from 'jotai';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Button } from '@tigergraph/app-ui-lib/button';
import { Checkbox } from '@tigergraph/app-ui-lib/checkbox';
import { TableBuilder } from '@tigergraph/app-ui-lib/table';
import { styled, useStyletron } from '@tigergraph/app-ui-lib/Theme';
import { Body2 } from '@tigergraph/app-ui-lib/typography';
import { ChevronDown, Plus } from 'baseui/icon';
import { ColumnOverrides, TableBuilderColumn } from 'baseui/table-semantic';
import clsx from 'clsx';

import SchemaIcon from '@/assets/graph-schema.svg?react';
import { actionColumnOverrides } from '@/components/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useOrgContext } from '@/contexts/orgContext';
import { parseDate } from '@/lib/date';
import { calculateRoleForGroup } from '@/pages/admin/user/type';
import { EditorIcon, ExplorerIcon, IngestionIcon } from '@/pages/home/icons';
import { defaultOnboardingState, onboardingAtom } from '@/pages/onboarding/state';
import { useMutationDeleteDatabase } from '@/pages/workgroup/hook';
import DatabaseIcon from '@/pages/workgroup/icons/database.svg?react';
import { StatusIndicator } from '@/pages/workgroup/tab/General';
import { ConfirmDeleteDatabase, SettingMenuForWorkSpace } from '@/pages/workgroup/tab/SettingMenu';
import { DatabaseNamePopover } from '@/pages/workgroup/tab/general/DatabaseNamePopover';
import { WorkspaceNamePopover } from '@/pages/workgroup/tab/general/WorkspaceNamePopover';
import { DatabaseT, WorkGroupT, WorkspaceT, isStatusActive } from '@/pages/workgroup/type';
import trackUtil from '@/utils/analytics-service.ts';
import { getErrorMessage } from '@/utils/utils';

import { SnapshotTag } from './SnapshotTag';
import { formatConfigurationLabel, orderWorkspaceRowsForCardView } from './utils';
import { WorkspaceRow } from './types';

const QUICK_ACCESS_COLOR = '#155DFC';

const QuickAccessIcon = styled('span', ({ $theme }) => ({
  width: '18px',
  height: '18px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  color: $theme.colors['icon.primary'],
}));

const centerColumnOverrides: ColumnOverrides = {
  TableHeadCell: { style: { verticalAlign: 'middle' } },
  TableBodyCell: { style: { verticalAlign: 'middle' } },
};

const centeredActionColumnOverrides: ColumnOverrides = {
  ...actionColumnOverrides,
  TableHeadCell: {
    ...actionColumnOverrides.TableHeadCell,
    style: {
      ...(actionColumnOverrides.TableHeadCell as any)?.style,
      verticalAlign: 'middle',
    },
  },
  TableBodyCell: {
    ...actionColumnOverrides.TableBodyCell,
    style: {
      ...(actionColumnOverrides.TableBodyCell as any)?.style,
      verticalAlign: 'middle',
    },
  },
};

type WorkspaceTableRow = { kind: 'workspace'; row: WorkspaceRow };
type OrphanTableRow = { kind: 'orphan'; database: DatabaseT };
type TableRow = WorkspaceTableRow | OrphanTableRow;

export type TableViewProps = {
  group: WorkGroupT;
  rows: WorkspaceRow[];
  orphanDatabases: DatabaseT[];
  selectedIds: Set<string>;
  selectedDatabaseIds: Set<string>;
  onToggleRow: (workspace: WorkspaceT) => void;
  onToggleDatabase: (database: DatabaseT) => void;
  onToggleAll: (selectAll: boolean) => void;
  isCreatingDisabled: boolean;
  canCreate: boolean;
  onCreate: () => void;
  emptyMessage?: string;
};

export default function TableView({
  group,
  rows,
  orphanDatabases,
  selectedIds,
  selectedDatabaseIds,
  onToggleRow,
  onToggleDatabase,
  onToggleAll,
  isCreatingDisabled,
  canCreate,
  onCreate,
  emptyMessage,
}: TableViewProps) {
  const [css, theme] = useStyletron();

  const orderedRows = useMemo(() => orderWorkspaceRowsForCardView(rows), [rows]);

  const tableData = useMemo<TableRow[]>(
    () => [
      ...orderedRows.map((row): WorkspaceTableRow => ({ kind: 'workspace', row })),
      ...orphanDatabases.map((database): OrphanTableRow => ({ kind: 'orphan', database })),
    ],
    [orderedRows, orphanDatabases]
  );

  const allSelectableIds = orderedRows.map((row) => row.workspace.workspace_id);
  const allChecked = allSelectableIds.length > 0 && allSelectableIds.every((id) => selectedIds.has(id));
  const someChecked = allSelectableIds.some((id) => selectedIds.has(id));
  const isIndeterminate = someChecked && !allChecked;

  const databaseById = useMemo(
    () =>
      group.tg_databases.reduce<Record<string, DatabaseT>>((acc, database) => {
        acc[database.database_id] = database;
        return acc;
      }, {}),
    [group.tg_databases]
  );

  if (tableData.length === 0) {
    return (
      <div
        className={css({
          padding: '32px 16px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '12px',
          color: theme.colors['text.secondary'],
          backgroundColor: theme.colors['background.alternative'],
          borderRadius: '12px',
          border: `1px solid ${theme.colors.divider}`,
        })}
      >
        <span>{emptyMessage || 'No workspaces match your filters.'}</span>
        {canCreate ? (
          <Button
            kind="secondary"
            size="compact"
            disabled={isCreatingDisabled}
            onClick={onCreate}
            startEnhancer={<Plus size={16} />}
          >
            Create Workspace
          </Button>
        ) : null}
      </div>
    );
  }

  return (
    <div
      className={css({
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        backgroundColor: theme.colors['background.alternative'],
        borderRadius: '12px',
        border: `1px solid ${theme.colors.divider}`,
        overflow: 'hidden',
      })}
    >
      <TableBuilder data={tableData} emptyMessage={null}>
        <TableBuilderColumn<TableRow>
          id="select"
          overrides={{
            TableHeadCell: { style: { width: '48px', verticalAlign: 'middle' } },
            TableBodyCell: { style: { width: '48px', verticalAlign: 'middle' } },
          }}
          header={
            <Checkbox
              aria-label="Select all workspaces"
              checked={allChecked}
              isIndeterminate={isIndeterminate}
              onChange={(e) => onToggleAll((e.target as HTMLInputElement).checked)}
            />
          }
        >
          {(item) =>
            item.kind === 'workspace' ? (
              <Checkbox
                aria-label={`Select workspace ${item.row.workspace.name}`}
                checked={selectedIds.has(item.row.workspace.workspace_id)}
                onChange={() => onToggleRow(item.row.workspace)}
              />
            ) : (
              <Checkbox
                aria-label={`Select database ${item.database.name}`}
                checked={selectedDatabaseIds.has(item.database.database_id)}
                onChange={() => onToggleDatabase(item.database)}
              />
            )
          }
        </TableBuilderColumn>

        <TableBuilderColumn<TableRow> id="name" header="WORKSPACE / DATABASE" overrides={centerColumnOverrides}>
          {(item) => {
            if (item.kind === 'orphan') {
              return <OrphanNameCell database={item.database} />;
            }
            const database = item.row.workspace.database_id ? databaseById[item.row.workspace.database_id] : undefined;
            const databaseName = database?.name || item.row.databaseName;
            const relatedRwWorkspace =
              !item.row.workspace.is_rw && item.row.workspace.database_id
                ? group.workspaces.find((w) => w.is_rw && w.database_id === item.row.workspace.database_id)
                : undefined;
            return (
              <div
                className={css({
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '4px',
                })}
              >
                <div className={css({ display: 'inline-flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' })}>
                  <WorkspaceNamePopover
                    workspace={item.row.workspace}
                    database={database}
                    group={group}
                    className={css({
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                      fontSize: '14px',
                      fontWeight: 500,
                      color: theme.colors['text.primary'],
                    })}
                  >
                    {item.row.workspace.name}
                  </WorkspaceNamePopover>
                  {!item.row.workspace.is_rw ? (
                    <SnapshotTag snapshot={item.row.workspace} rwWorkspace={relatedRwWorkspace} />
                  ) : null}
                </div>
                {databaseName ? (
                  <span
                    className={css({
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                      fontSize: '12px',
                      color: theme.colors['text.secondary'],
                    })}
                  >
                    {databaseName}
                  </span>
                ) : null}
              </div>
            );
          }}
        </TableBuilderColumn>

        <TableBuilderColumn<TableRow> id="status" header="STATUS" overrides={centerColumnOverrides}>
          {(item) =>
            item.kind === 'workspace' ? (
              <div
                className={css({
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontSize: '12px',
                  padding: '6px 12px',
                  backgroundColor: theme.colors['tag.background.default'],
                  borderRadius: '14px',
                })}
              >
                <StatusIndicator
                  status={item.row.workspace.status}
                  errMessage={item.row.workspace.err_message}
                  size="compact"
                />
                <span>{item.row.workspace.status}</span>
              </div>
            ) : (
              <span>-</span>
            )
          }
        </TableBuilderColumn>

        <TableBuilderColumn<TableRow> id="access" header="ACCESS" overrides={centerColumnOverrides}>
          {(item) =>
            item.kind === 'workspace' ? (
              <span
                className={clsx(
                  css({
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minWidth: '40px',
                    padding: '2px 8px',
                    borderRadius: '4px',
                    border: `1px solid ${item.row.workspace.is_rw ? '#BEDBFF' : '#E5E7EB'}`,
                    color: item.row.workspace.is_rw ? '#1447E6' : '#364153',
                    fontSize: '12px',
                    fontWeight: 500,
                    backgroundColor: item.row.workspace.is_rw ? '#EFF6FF' : '#F9FAFB',
                  })
                )}
              >
                {item.row.workspace.is_rw ? 'R/W' : 'R/O'}
              </span>
            ) : (
              <span>-</span>
            )
          }
        </TableBuilderColumn>

        <TableBuilderColumn<TableRow> id="configuration" header="CONFIGURATION" overrides={centerColumnOverrides}>
          {(item) =>
            item.kind === 'workspace' ? (
              <span className={css({ fontSize: '14px', color: theme.colors['text.primary'] })}>
                {formatConfigurationLabel(item.row.workspace)}
              </span>
            ) : (
              <span>-</span>
            )
          }
        </TableBuilderColumn>

        <TableBuilderColumn<TableRow> id="lastActivity" header="LAST ACTIVITY" overrides={centerColumnOverrides}>
          {(item) => {
            if (item.kind === 'orphan') {
              return format(parseDate(item.database.create_time), 'yyyy-MM-dd HH:mm');
            }
            const value = item.row.workspace.last_modified_time || item.row.workspace.created_at;
            if (!value) {
              return '-';
            }
            return format(parseDate(value), 'yyyy-MM-dd HH:mm');
          }}
        </TableBuilderColumn>

        <TableBuilderColumn<TableRow> id="quickAccess" header="QUICK ACCESS" overrides={centerColumnOverrides}>
          {(item) => (item.kind === 'workspace' ? <QuickAccess workspace={item.row.workspace} /> : <span>-</span>)}
        </TableBuilderColumn>

        <TableBuilderColumn<TableRow> header="ACTIONS" overrides={centeredActionColumnOverrides}>
          {(item) =>
            item.kind === 'workspace' ? (
              <div className={css({ display: 'flex', gap: '6px' })}>
                <SettingMenuForWorkSpace
                  workspace={item.row.workspace}
                  database={item.row.workspace.database_id ? databaseById[item.row.workspace.database_id] : undefined}
                  borderless
                  showNavItems
                />
              </div>
            ) : (
              <OrphanActions database={item.database} />
            )
          }
        </TableBuilderColumn>
      </TableBuilder>
    </div>
  );
}

function OrphanNameCell({ database }: { database: DatabaseT }) {
  const [css, theme] = useStyletron();
  return (
    <div className={css({ display: 'flex', flexDirection: 'column', gap: '4px' })}>
      <DatabaseNamePopover
        database={database}
        className={css({
          fontSize: '14px',
          fontWeight: 500,
          color: theme.colors['text.primary'],
        })}
      >
        {database.name}
      </DatabaseNamePopover>
      <span
        className={css({
          fontSize: '12px',
          color: '#B85C00',
          fontWeight: 500,
        })}
      >
        No workspace associated
      </span>
    </div>
  );
}

function OrphanActions({ database }: { database: DatabaseT }) {
  const { userInfo } = useOrgContext();
  const deleteDatabaseMutation = useMutationDeleteDatabase();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const isGroupAdmin = calculateRoleForGroup(userInfo.roles, database.workgroup_id) === 'workgroup-admins';

  const onDelete = () => {
    const promise = deleteDatabaseMutation.mutateAsync({
      group_id: database.workgroup_id,
      db_id: database.database_id,
    });

    toast.promise(promise, {
      loading: 'Deleting database',
      success: (result) => result.Message!,
      error: (err) => `${getErrorMessage(err)}`,
    });
  };

  if (!isGroupAdmin) {
    return <span>-</span>;
  }

  return (
    <>
      <Button kind="secondary" size="compact" onClick={() => setShowDeleteConfirm(true)}>
        Terminate
      </Button>
      {showDeleteConfirm ? (
        <ConfirmDeleteDatabase
          isOpen={showDeleteConfirm}
          onCancel={() => setShowDeleteConfirm(false)}
          onConfirm={() => {
            setShowDeleteConfirm(false);
            onDelete();
          }}
          name={database.name}
        />
      ) : null}
    </>
  );
}

function QuickAccess({ workspace }: { workspace: WorkspaceT }) {
  const [css, theme] = useStyletron();
  const navigate = useNavigate();
  const [, setOnboardingState] = useAtom(onboardingAtom);

  const enabled = isStatusActive(workspace.status) && workspace.canAccess;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild disabled={!enabled}>
        <button
          type="button"
          disabled={!enabled}
          aria-label="Quick Access"
          className={css({
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
            fontSize: '13px',
            fontWeight: 500,
            color: enabled ? QUICK_ACCESS_COLOR : theme.colors['text.secondary'],
            background: 'none',
            border: 'none',
            padding: 0,
            cursor: enabled ? 'pointer' : 'not-allowed',
          })}
        >
          Query Editor Schema + 5
          <ChevronDown size={14} color={enabled ? QUICK_ACCESS_COLOR : theme.colors['text.secondary']} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        className={css({
          width: '240px',
        })}
        align="start"
      >
        <DropdownMenuItem
          onClick={() => {
            trackUtil.trackEnterGSQLEditor('table view');
            navigate(`/editor?workspace_id=${encodeURIComponent(workspace.workspace_id)}`);
          }}
        >
          <QuickAccessIcon>
            <EditorIcon />
          </QuickAccessIcon>
          <Body2>Query Editor</Body2>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate('/design-schema?workspace_id=' + workspace.workspace_id)}>
          <QuickAccessIcon $style={{ marginTop: '-2px' }}>
            <SchemaIcon width={18} height={18} />
          </QuickAccessIcon>
          <Body2>{workspace.is_rw ? 'Design Schema' : 'View Schema'}</Body2>
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => {
            setOnboardingState({
              ...defaultOnboardingState,
              workspace,
            });
            navigate(`/onboarding/import-csv?workspace_id=${workspace.workspace_id}&source=table_view`);
          }}
        >
          <QuickAccessIcon>
            <DatabaseIcon color={theme.colors['icon.primary']} />
          </QuickAccessIcon>
          <Body2>Build AI</Body2>
        </DropdownMenuItem>
        {workspace.is_rw ? (
          <DropdownMenuItem onClick={() => navigate('/ingestion?workspace_id=' + workspace.workspace_id)}>
            <QuickAccessIcon>
              <IngestionIcon />
            </QuickAccessIcon>
            <Body2>Load Data</Body2>
          </DropdownMenuItem>
        ) : null}
        <DropdownMenuItem
          onClick={() => navigate(`/explore?workspace_id=${encodeURIComponent(workspace.workspace_id)}`)}
        >
          <QuickAccessIcon>
            <ExplorerIcon />
          </QuickAccessIcon>
          <Body2>Explore Graph</Body2>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
